/**
 * SharingControls Component
 * Reusable form controls for document/FAQ sharing settings
 */

import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DocumentVisibility } from '../../api/data-contracts';
import { RecipientPicker } from './RecipientPicker';
import { RecipientSelection } from './RecipientPicker/types';

export interface SharingFormData {
  visibility: DocumentVisibility;
  recipientSelection: RecipientSelection;
  purpose: string;
}

interface SharingControlsProps {
  /** Current sharing form data */
  data: SharingFormData;
  /** Callback when data changes */
  onChange: (data: Partial<SharingFormData>) => void;
  /** Whether controls are disabled */
  disabled?: boolean;
  showRoles?: boolean;
  /** Whether to show purpose field */
  showPurpose?: boolean;
}

export const SharingControls: React.FC<SharingControlsProps> = ({
  data,
  onChange,
  disabled = false,
  showPurpose = true
}) => {
  const { t } = useTranslation();
  const selection = data.recipientSelection;

  const handleVisibilityChange = (event: SelectChangeEvent) => {
    onChange({
      visibility: event.target.value as DocumentVisibility
    });
  };

  const handleRecipientsChange = (nextSelection: RecipientSelection) => {
    onChange({
      recipientSelection: nextSelection,
    });
  };

  const handlePurposeChange = (event: SelectChangeEvent) => {
    onChange({
      purpose: event.target.value
    });
  };

  return (
    <Box>
      {/* Visibility Setting */}
      <FormControl fullWidth margin="normal" disabled={disabled}>
        <InputLabel>{t('documents.visibility.label')}</InputLabel>
        <Select
          value={data.visibility || DocumentVisibility.Private}
          onChange={handleVisibilityChange}
          label={t('documents.visibility.label')}
        >
          <MenuItem value={DocumentVisibility.Private}>
            {t('documents.visibility.private')}
          </MenuItem>
          <MenuItem value={DocumentVisibility.Organization}>
            {t('documents.visibility.organization')}
          </MenuItem>
          <MenuItem value={DocumentVisibility.Group}>
            {t('documents.visibility.group')}
          </MenuItem>
        </Select>
      </FormControl>

      {/* Recipient Selection for Group visibility */}
      {((data.visibility || DocumentVisibility.Private) === DocumentVisibility.Group || (data.visibility || DocumentVisibility.Private) === DocumentVisibility.Private) && (
        <Box sx={{ mt: 2 }}>
          <RecipientPicker
            variant="side-by-side"
            label={t('sharing.recipients.label')}
            value={selection}
            onChange={handleRecipientsChange}
            placeholder={t('sharing.recipients.placeholder')}
            searchConfig="sharing"
            disabled={disabled}
          />
        </Box>
      )}

      {/* Purpose Field (optional) */}
      {showPurpose && (
        <FormControl fullWidth margin="normal" disabled={disabled}>
          <InputLabel>{t('sharing.purpose.label')}</InputLabel>
          <Select
            value={data.purpose}
            onChange={handlePurposeChange}
            label={t('sharing.purpose.label')}
          >
            <MenuItem value="general">{t('sharing.purpose.general')}</MenuItem>
            <MenuItem value="contract_analysis">{t('sharing.purpose.contractAnalysis')}</MenuItem>
            <MenuItem value="knowledge_base">{t('sharing.purpose.knowledgeBase')}</MenuItem>
            <MenuItem value="training_material">{t('sharing.purpose.trainingMaterial')}</MenuItem>
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

export default SharingControls;
